import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { Project } from 'src/app/shared/Models/project-model';

@Component({
    selector: 'ri-toolbar-project-selector',
    imports: [
    FormsModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    TranslatePipe
],
    templateUrl: './toolbar-project-selector.component.html',
    styleUrls: ['./toolbar-project-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToolbarProjectSelectorComponent implements OnChanges {
  @Input() projects: Project[] | null = [];
  @Input() selectedProject: number | null = null;
  @Input() showLoading = false;
  @Input() variant: 'toolbar' | 'sidenav' = 'toolbar';

  @HostBinding('class.sidenav-mode') get isSidenavMode() { return this.variant === 'sidenav'; }

  @Output() selectedProjectChange = new EventEmitter<number>();

  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  currentLabel = '';
  currentProject: Project | null = null;
  searchTerm = '';
  filteredProjects: Project[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projects'] || changes['selectedProject']) {
      this.filteredProjects = this.applyFilter(this.projects ?? []);
      // Always show what the store says is current. A project missing from the list (stale or
      // refused) shows "select project" instead of a label the backend did not accept.
      const found = (this.projects ?? []).find(p => p.id === this.selectedProject) ?? null;
      this.currentProject = found;
      this.currentLabel = found ? this.buildLabel(this.selectedProject) : '';
    }
  }

  onMenuOpened(): void {
    this.searchTerm = '';
    this.filteredProjects = this.projects ?? [];
    // Defer focus so the menu panel is fully in the DOM
    setTimeout(() => {
      this.searchInputRef?.nativeElement?.focus();
      this.cdr.markForCheck();
    }, 50);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    // Let Escape bubble so mat-menu closes; swallow everything else to prevent
    // mat-menu keyboard navigation from interfering with the search input.
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  }

  filterProjects(): void {
    this.filteredProjects = this.applyFilter(this.projects ?? []);
  }

  selectProject(id: number): void {
    if (id === this.selectedProject) return;
    // No optimistic label: the selector reflects the store, which changes only once the backend
    // has accepted the new project.
    this.selectedProjectChange.emit(id);
  }

  private applyFilter(list: Project[]): Project[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return list;
    return list.filter(p =>
      p.name.toLowerCase().includes(term) ||
      p.costCenterNumber.toLowerCase().includes(term)
    );
  }

  private buildLabel(id: number | null): string {
    const list = this.projects ?? [];
    const project = list.find(p => p.id === id);
    return project ? `${project.costCenterNumber}-${project.name}` : '';
  }
}
